<template>
    <div class="sms-broadcast-page">
        <div
            class="verification-status"
            data-qa="ktv-sms-verification-status"
        >
            <KtvSmsVerificationStatusAlert
                render-if-sending-blocked
                show-button
                :message="smsAccountVerification.errorDescription"
                :rejection-date="smsAccountVerification.rejectionDate"
                :verification-status="smsAccountVerification.status"
            />
        </div>

        <div class="filter">
            <ds-input-field
                id="search-entry"
                v-model="search"
                :label="$options.MESSAGE_CONSTANTS.SEARCH"
                type="search"
            />
            <SmsBroadcastFilter
                class="filter-type"
                :filter.sync="filter"
            />
        </div>
        <EmptyResults
            :filter="filter.label"
            :no-filter-results="noFilterResults"
            :no-search-results="noSearchResults"
            :search="search"
        />
        <SmsBroadcastItem
            v-for="(broadcast, i) in broadcasts"
            :key="broadcast.id"
            :index="i"
            :broadcast="broadcast"
            @delete="deleteBroadcast"
            @copy="copy"
            @edit="edit"
            @resume="resume"
            @stop="stopBroadcast"
            @open="openBroadcast"
        />
        <ds-infinite-scroll
            :limit.sync="limit"
            :loading="loading"
            :no-more-data="noMoreData"
            @load="infiniteScrollLoad"
        >
            <div class="placeholders">
                <div v-for="i in 3" :key="i" class="placeholder">
                    <ds-placeholder
                        :rows="placeholder"
                        :loading="loading"
                    />
                </div>
            </div>
        </ds-infinite-scroll>
        <SmsBroadcastSummaryModal
            v-if="showSummaryModal"
            :is-open="showSummaryModal"
            :broadcast="summaryElement"
            @close="closeSummaryModal"
            @copy="copy"
            @resume="resume"
            @stop="stopBroadcast"
        />

        <KmnVerificationForm
            v-if="showKmnVerificationModal"
            :is-open="isKmnVerificationModalOpen"
            :kmn-account="account"
            :source="kmnVerificationSource"
            :is-kmn-branded-opt-in-enabled="isKmnBrandedOptInEnabled"
            :kmn-phone-verification-enabled="kmnPhoneVerificationEnabled"
            data-qa="kmn-verification-modal"
            @close="handleCloseKmnVerificationForm"
            @complete="handleCloseKmnVerificationForm"
            @copy-broadcast="copyBroadcast"
            @edit-broadcast="editBroadcast"
            @resume-broadcast="resumeBroadcast"
            @stop-broadcast="stopBroadcast"
        />
    </div>
</template>

<script>
import debounce from 'lodash.debounce';
import SmsBroadcastItem from '@/components/broadcast/sms/SmsBroadcastItem';
import SmsBroadcastFilter from '@/components/broadcast/sms/SmsBroadcastFilter';
import SmsBroadcastSummaryModal from '@/components/broadcast/sms/SmsBroadcastSummaryModal';
import KmnVerificationForm from '@/components/broadcast/sms/KmnVerificationForm';
import KtvSmsVerificationStatusAlert from '@/components/automatedSms/KtvSmsVerificationStatusAlert';

import EmptyResults from '@/components/shared/EmptyResults';
import { SMS_BROADCAST_STATUS, MESSAGE_CONSTANTS, KMN_VERIFICATION_STATUS_TYPE } from '../constants/smsBroadcast.constants';
import { SEARCHING_DEBOUNCE_DELAY } from '@/components/shared/constants/timing.constants';
import {
    resumeSmsBroadcast,
    stopSmsBroadcast,
    deleteSmsBroadcast,
} from '@/api/broadcasts/smsBroadcasts';

const VERIFICATION_DISPLAY_TYPES = [
    KMN_VERIFICATION_STATUS_TYPE.EXPIRED,
    KMN_VERIFICATION_STATUS_TYPE.RESUBMISSION,
    KMN_VERIFICATION_STATUS_TYPE.NOT_VERIFIED,
];

export default {
    name: 'SmsBroadcastPage',

    MESSAGE_CONSTANTS,
    SMS_BROADCAST_STATUS,
    KMN_VERIFICATION_STATUS_TYPE,
    resumeSmsBroadcast,
    stopSmsBroadcast,
    deleteSmsBroadcast,

    components: {
        SmsBroadcastItem,
        SmsBroadcastFilter,
        SmsBroadcastSummaryModal,
        EmptyResults,
        KmnVerificationForm,
        KtvSmsVerificationStatusAlert,
    },

    props: {
        appId: {
            type: String,
            default: '',
        },
        userId: {
            type: Number,
            default: 0,
        },
        broadcasts: {
            type: Array,
            required: true,
        },
        loading: {
            type: Boolean,
            required: true,
        },
        limit: {
            type: Number,
            required: true,
        },
        noMoreData: {
            type: Boolean,
            required: true,
        },
        debounceDelay: {
            type: Number,
            default: SEARCHING_DEBOUNCE_DELAY,
        },
        account: {
            type: Object,
            required: true,
        },
        smsAccountVerification: {
            type: Object,
            default: () => ({}),
        },
        isKmnBrandedOptInEnabled: Boolean,
        kmnPhoneVerificationEnabled: Boolean,
    },

    data () {
        return {
            showSummaryModal: false,
            summaryElement: {},
            filter: {},
            search: '',
            placeholder: [
                { height: '5rem', boxes: [2, 1, 1, 1] },
            ],
            kmnVerificationSource: 'Copy broadcast',
            isKmnVerificationModalOpen: false,
            broadcastId: 0,
        };
    },

    created() {
        if (this.debounceDelay > 0) {
            this.updateSearch = debounce(this.updateSearch, this.debounceDelay);
        }
    },

    watch: {
        filter() {
            this.updateFilter();
        },
        search() {
            this.updateSearch();
        },
    },

    computed: {
        noFilterResults() {
            return !this.loading && Boolean(this.filter.value) && this.broadcasts.length === 0;
        },

        noSearchResults() {
            return !this.loading && Boolean(this.search) && this.broadcasts.length === 0;
        },
        isFormValidToDisplay() {
            return !this.account?.verificationStatus || VERIFICATION_DISPLAY_TYPES.includes(this.account.verificationStatus);
        },
        showKmnVerificationModal() {
            return this.account?.phoneNumber && this.kmnPhoneVerificationEnabled && this.isFormValidToDisplay;
        },
    },

    methods: {
        copy(id) {
            this.broadcastId = id;

            if (this.showKmnVerificationModal) {
                this.kmnVerificationSource = 'Copy broadcast';
                this.isKmnVerificationModalOpen = true;
            } else {
                this.copyBroadcast();
            }
        },

        copyBroadcast() {
            this.$emit('copy-broadcast', this.broadcastId);
            this.closeSummaryModal();
        },

        edit(id) {
            this.broadcastId = id;

            if (this.showKmnVerificationModal) {
                this.kmnVerificationSource = 'Edit broadcast';
                this.isKmnVerificationModalOpen = true;
            } else {
                this.editBroadcast();
            }
        },

        editBroadcast() {
            this.$emit('edit-broadcast', this.broadcastId);
            this.closeSummaryModal();
        },


        resume(id) {
            this.broadcastId = id;

            if (this.showKmnVerificationModal) {
                this.kmnVerificationSource = 'Resume broadcast';
                this.isKmnVerificationModalOpen = true;
            } else {
                this.resumeBroadcast();
            }
        },

        async deleteBroadcast(id) {
            const vm = this;

            try {
                await this.$confirm({
                    destructive: true,
                    title: MESSAGE_CONSTANTS.DELETE_CONFIRM_TITLE,
                    message: MESSAGE_CONSTANTS.DELETE_CONFIRM_MESSAGE,
                    confirmLabel: MESSAGE_CONSTANTS.DELETE_CONFIRM_BUTTON_TEXT,
                    cancelLabel: MESSAGE_CONSTANTS.DELETE_CANCEL_BUTTON_TEXT,
                });
            } catch (error) {
                return;
            }

            this.deleting = true;

            deleteSmsBroadcast(id)
                .then(() => {
                    vm.reload();
                    this.$toast({ message: MESSAGE_CONSTANTS.BROADCAST_DELETE_SUCCESS });
                    this.deleting = false;
                })
                .catch(() => {
                    this.$error({ message: MESSAGE_CONSTANTS.BROADCAST_DELETE_ERROR });
                    this.deleting = false;
                });
        },

        async stopBroadcast(id) {
            const vm = this;
            const command = {
                id,
                updatedBy: this.userId,
            };

            stopSmsBroadcast(command)
                .then(() => {
                    vm.reload();
                    this.$toast({ message: MESSAGE_CONSTANTS.BROADCAST_STOP_SUCCESS });
                })
                .catch(() => {
                    this.$error({ message: MESSAGE_CONSTANTS.BROADCAST_STOP_ERROR });
                });
        },

        async resumeBroadcast() {
            const command = {
                id: this.broadcastId,
                updatedBy: this.userId,
            };

            try {
                const resumedBroadcast = await resumeSmsBroadcast(command);

                if (resumedBroadcast.status === SMS_BROADCAST_STATUS.SCHEDULED) {
                    this.$emit('resume-broadcast', this.broadcastId);
                    this.closeSummaryModal();
                } else {
                    this.editBroadcast();
                }
            } catch (error) {
                this.$error({ message: MESSAGE_CONSTANTS.BROADCAST_RESUME_ERROR });
            }
        },

        openBroadcast(broadcast) {
            if (broadcast.status === SMS_BROADCAST_STATUS.DRAFT) {
                this.edit(broadcast.id);
            } else {
                this.openSummaryModal(broadcast);
            }
        },

        closeSummaryModal() {
            this.showSummaryModal = false;
        },

        openSummaryModal(broadcast) {
            this.showSummaryModal = true;
            this.summaryElement = broadcast;
        },

        infiniteScrollLoad() {
            if (!this.noMoreData) {
                this.load();
            }
        },

        reload() {
            this.$emit('reload');
        },

        load() {
            this.$emit('load');
        },

        updateFilter() {
            this.$emit('filter-updated', this.filter);
        },

        updateSearch() {
            this.$emit('search-updated', this.search);
        },

        handleCloseKmnVerificationForm() {
            this.isKmnVerificationModalOpen = false;

            this.$emit('kmn-verification-form-closed');
        },
    },
};
</script>

<style lang="scss">
    div.sms-broadcast-page {
        display: block;
    }

    div.sms-broadcast-page div#search-entry {
        width: 350px;
        margin-bottom: 0;
    }

    div.sms-broadcast-page div#search-entry > input{
        height: 24px;
    }

    div.sms-broadcast-page div.filter {
        display: inline-grid;
        grid-template-columns: 90% 10%;
        align-items: center;
        width: 100%;
        padding-bottom: .75rem;
    }
</style>
